import { Link, useLocation, useNavigate } from "react-router";
import {
  LayoutDashboard, Users, Home, TrendingUp, Settings,
  Bell, Search, Plus, LogOut, LifeBuoy, BarChart3, Menu, X,
  Store, PieChart, BookUser, Shield, CheckSquare, Mail, Send,
  AlertTriangle, CreditCard,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "../supabase";
import { insertLeads } from "../lib/data/leads";
import { getUserRole, type UserRole } from "../lib/auth";
import type { User } from "@supabase/supabase-js";
import { useSubscription } from "../hooks/useSubscription";
import { PlanBadge } from "./PlanBadge";
import { DuplicateWarningModal, type DupLead } from "./DuplicateWarningModal";
import type { Database } from "~/types/database.types";

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showNewLead, setShowNewLead] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [newLeadSubmitting, setNewLeadSubmitting] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  // Aviso de posibles duplicados (mismo teléfono/email) al crear prospecto
  type PendingLead = { name: string; phone: string; email: string; budget: string };
  const [dupWarning, setDupWarning] = useState<DupLead[]>([]);
  const [pendingLead, setPendingLead] = useState<PendingLead | null>(null);

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (e.defaultPrevented) return;
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotifications(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const insertLead = async (values: PendingLead) => {
    setNewLeadSubmitting(true);
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    if (!currentUser) { setNewLeadSubmitting(false); return; }
    const { error } = await insertLeads([{
      user_id: currentUser.id,
      name: values.name,
      phone: values.phone || null,
      email: values.email || null,
      budget: values.budget ? Number(values.budget) : null,
      source: "Orgánico",
      status: "Nuevo",
    }]);
    setNewLeadSubmitting(false);
    if (!error) { setShowNewLead(false); setDupWarning([]); setPendingLead(null); }
  };

  const handleNewLeadSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const name = (formData.get("name") as string)?.trim() || "";
    const phone = (formData.get("phone") as string)?.trim() || "";
    const email = (formData.get("email") as string)?.trim() || "";
    const budget = (formData.get("budget") as string) || "";
    if (!name) return;
    const values: PendingLead = { name, phone, email, budget };
    // Verificar posibles duplicados antes de insertar (avisar, no bloquear)
    if (phone || email) {
      setNewLeadSubmitting(true);
      // Los args generados marcan p_phone/p_email como string, pero la función
      // Postgres acepta null (limitación del codegen). Casteamos sin cambiar los
      // valores enviados en runtime.
      const { data, error } = await supabase.rpc("find_duplicate_leads", {
        p_phone: phone || null,
        p_email: email || null,
      } as unknown as Database["public"]["Functions"]["find_duplicate_leads"]["Args"]);
      setNewLeadSubmitting(false);
      if (!error && data && data.length > 0) {
        setPendingLead(values);
        setDupWarning(data as DupLead[]);
        return;
      }
    }
    await insertLead(values);
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) navigate("/login");
      else setUser(session.user);
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  // El user del token de sesión puede traer app_metadata.role desestualizado
  // (p. ej. si el rol se actualizó tras iniciar sesión). getUser() trae el rol
  // fresco del servidor para que el sidebar muestre los items correctos.
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user: freshUser } }) => {
      if (freshUser) setUser(freshUser);
    });
  }, []);

  const handleLogout = async () => { await supabase.auth.signOut(); };

  const { planId, isActive: subIsActive, loading: subLoading, subscription } = useSubscription();
  const role: UserRole = getUserRole(user);

  const navItems = [
    { name: "Inicio",            icon: LayoutDashboard, path: "/",                    roles: ["admin","broker","asesor","coordinador","marketing"] },
    { name: "Embudo",            icon: Users,           path: "/crm",                 roles: ["admin","broker","asesor","coordinador"] },
    { name: "Contactos",         icon: BookUser,        path: "/contactos",           roles: ["admin","broker","asesor","coordinador"] },
    { name: "Propiedades",       icon: Home,            path: "/propiedades",         roles: ["admin","broker","asesor","coordinador"] },
    { name: "Marketplace",       icon: Store,           path: "/marketplace",         roles: ["admin","broker","asesor","coordinador"] },
    { name: "Redes",             icon: TrendingUp,      path: "/redes",               roles: ["admin","broker","asesor","coordinador","marketing"] },
    { name: "Tareas",            icon: CheckSquare,     path: "/tareas",              roles: ["admin","broker","asesor","coordinador","marketing"] },
    { name: "Plantillas Email",  icon: Mail,            path: "/email-templates",     roles: ["admin","broker","asesor","coordinador","marketing"] },
    { name: "Campañas Email",    icon: Send,            path: "/email-campanas",      roles: ["admin","broker","asesor","coordinador","marketing"] },
    { name: "Páginas",           icon: LayoutDashboard, path: "/landing-builder",     roles: ["admin","broker","asesor","coordinador","marketing"] },
    { name: "Soporte",           icon: LifeBuoy,        path: "/soporte",             roles: ["admin","broker","asesor","coordinador","marketing"] },
    { name: "Reportes",          icon: PieChart,        path: "/reportes",            roles: ["admin","broker","asesor","coordinador","marketing"] },
    { name: "Reportes Equipo",   icon: BarChart3,       path: "/reportes-equipo",     roles: ["admin","broker","coordinador"] },
    { name: "Equipo",            icon: Shield,          path: "/equipo",              roles: ["admin","broker","coordinador","asesor","marketing"] },
    { name: "Admin",             icon: BarChart3,       path: "/admin",               roles: ["admin"] },
    { name: "Suscripciones",     icon: CreditCard,      path: "/admin/suscripciones", roles: ["admin"] },
  ].filter((item) => item.roles.includes(role));

  const isActivePath = (path: string) => location.pathname === path;
  const userDisplay =
    user?.user_metadata?.full_name?.substring(0, 2).toUpperCase() ||
    user?.email?.substring(0, 2).toUpperCase() ||
    "??";

  return (
    <div className="layout-root">
      {/* ── Overlay móvil ── */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={closeSidebar} />
      )}

      {/* ══════════════════════════════════════════
          SIDEBAR — azul patrimonio
      ══════════════════════════════════════════ */}
      <aside className={`sidebar${sidebarOpen ? " sidebar--open" : ""}`}>
        {/* Wordmark */}
        <div className="sidebar-brand">
          <span className="sidebar-brand__text">CRM</span>
          <span className="sidebar-brand__sub">INMOBILIARIO</span>
        </div>

        {/* Nav items */}
        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-nav-item${isActivePath(item.path) ? " sidebar-nav-item--active" : ""}`}
              onClick={closeSidebar}
            >
              <item.icon size={20} strokeWidth={2} />
              <span>{item.name}</span>
              {isActivePath(item.path) && (
                <span className="sidebar-active-bar" />
              )}
            </Link>
          ))}
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          <div style={{ paddingBottom: "0.5rem" }}>
            <PlanBadge planId={planId} isActive={subIsActive} loading={subLoading} />
          </div>
          <Link
            to="/ajustes"
            className={`sidebar-nav-item${isActivePath("/ajustes") ? " sidebar-nav-item--active" : ""}`}
            onClick={closeSidebar}
          >
            <Settings size={20} strokeWidth={2} />
            <span>Ajustes</span>
            {isActivePath("/ajustes") && (
              <motion.div
                layoutId="sidebar-active-bar"
                className="sidebar-active-bar"
                transition={{ type: "spring", stiffness: 380, damping: 30 }}
              />
            )}
          </Link>
          <button
            onClick={handleLogout}
            className="sidebar-nav-item sidebar-nav-item--logout"
            style={{ width: "100%", marginTop: "4px" }}
          >
            <LogOut size={20} strokeWidth={2} />
            <span>Cerrar Sesión</span>
          </button>
        </div>
      </aside>

      {/* ══════════════════════════════════════════
          MAIN VIEWPORT
      ══════════════════════════════════════════ */}
      <main className="main-viewport">
        {/* Header */}
        <header className="app-header">
          {/* Hamburger (solo móvil) */}
          <button
            className="mobile-menu-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Abrir menú"
          >
            {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          {/* Búsqueda */}
          <div className="header-search">
            <Search size={16} strokeWidth={2} className="header-search__icon" />
            <input
              type="text"
              placeholder="Buscar propiedades, leads..."
              className="header-search__input"
            />
          </div>

          {/* Acciones */}
          <div className="header-actions">
            {/* Notificaciones */}
            <div style={{ position: "relative" }} ref={notifRef}>
              <button
                type="button"
                className="header-icon-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowNotifications(!showNotifications);
                }}
                aria-label="Notificaciones"
              >
                <Bell size={20} strokeWidth={2} />
                <span className="notif-dot" />
              </button>
              {showNotifications && (
                <div className="notif-dropdown">
                  <div className="notif-dropdown__header">Notificaciones</div>
                  <div className="notif-dropdown__empty">No hay notificaciones nuevas.</div>
                </div>
              )}
            </div>

            {/* Nuevo Lead */}
            <button
              className="btn-primary header-new-lead-btn"
              onClick={() => setShowNewLead(true)}
            >
              <Plus size={18} strokeWidth={2} />
              <span>Nuevo Lead</span>
            </button>

            {/* Avatar */}
            <div className="header-avatar">{userDisplay}</div>
          </div>
        </header>

        {/* Banner past_due */}
        {subscription?.status === "past_due" && (
          <div className="payment-alert-banner">
            <AlertTriangle size={16} strokeWidth={2} />
            <span>
              Hay un problema con tu pago. Tu acceso puede verse limitado.{" "}
              <Link to="/ajustes?tab=suscripcion" className="payment-alert-link">
                Actualizar método de pago →
              </Link>
            </span>
          </div>
        )}

        {/* Contenido */}
        <section className="page-content">{children}</section>
      </main>

      {/* ══════════════════════════════════════════
          MODAL — Nuevo Lead
      ══════════════════════════════════════════ */}
      <AnimatePresence>
        {showNewLead && (
          <div className="modal-backdrop" onClick={() => setShowNewLead(false)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.15 }}
              className="modal-box"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Nuevo Prospecto</h2>
                <button className="modal-close-btn" onClick={() => setShowNewLead(false)}>
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleNewLeadSubmit} className="modal-form">
                <div className="form-field">
                  <label>Nombre Completo *</label>
                  <input type="text" name="name" required placeholder="Ej. Juan Pérez" />
                </div>
                <div className="form-row">
                  <div className="form-field">
                    <label>Teléfono</label>
                    <input type="tel" name="phone" placeholder="555-1234" />
                  </div>
                  <div className="form-field">
                    <label>Email</label>
                    <input type="email" name="email" placeholder="juan@ejemplo.com" />
                  </div>
                </div>
                <div className="form-field">
                  <label>Presupuesto (MXN)</label>
                  <input type="number" name="budget" placeholder="Ej. 2500000" />
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-secondary" onClick={() => setShowNewLead(false)}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary" disabled={newLeadSubmitting}>
                    {newLeadSubmitting ? "Guardando…" : "Guardar Prospecto"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Aviso de posibles duplicados al crear prospecto */}
      <DuplicateWarningModal
        duplicates={dupWarning}
        onCancel={() => { setDupWarning([]); setPendingLead(null); }}
        onConfirm={() => { if (pendingLead) insertLead(pendingLead); }}
        onView={(id) => {
          setDupWarning([]);
          setPendingLead(null);
          setShowNewLead(false);
          navigate(`/contactos/${id}`);
        }}
      />

      <style>{`
        /* ── Layout shell ── */
        .layout-root    { display: flex; min-height: 100vh; background: var(--background); }

        /* ── Sidebar ── */
        .sidebar {
          width: var(--sidebar-w);
          background-color: var(--brand-800);
          padding: 28px 16px;
          display: flex;
          flex-direction: column;
          position: fixed;
          height: 100vh;
          overflow: hidden;
          z-index: 200;
          transition: transform 0.25s ease;
        }

        /* Wordmark */
        .sidebar-brand          { display: flex; flex-direction: column; padding: 0 14px; margin-bottom: 32px; }
        .sidebar-brand__text    { font-family: var(--font-display); font-size: 1.375rem; font-weight: 600; color: #fff; letter-spacing: -0.01em; line-height: 1.2; }
        .sidebar-brand__sub     { font-size: 0.6875rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: var(--gold-600); margin-top: 1px; }

        /* Nav items */
        .sidebar-nav { flex: 1; display: flex; flex-direction: column; gap: 2px; overflow-y: auto; min-height: 0; }
        .sidebar-nav-item {
          display: flex; align-items: center; gap: 12px; width: 100%;
          padding: 10px 14px; border-radius: var(--radius-md);
          color: rgba(255,255,255,0.68); font-size: 0.9375rem; font-weight: 500;
          position: relative; transition: background var(--transition-fast), color var(--transition-fast);
          background: none; border: none; text-align: left; cursor: pointer; text-decoration: none;
        }
        .sidebar-nav-item:hover { background: rgba(255,255,255,0.08); color: #fff; }
        .sidebar-nav-item--active {
          background: rgba(255,255,255,0.13);
          color: #fff;
          font-weight: 600;
        }
        .sidebar-active-bar {
          position: absolute; left: -16px; top: 50%; transform: translateY(-50%);
          width: 3px; height: 20px;
          background: var(--gold-600); border-radius: 0 3px 3px 0;
        }
        .sidebar-nav-item--logout { color: rgba(255,255,255,0.5); }
        .sidebar-nav-item--logout:hover { color: #fca5a5; background: rgba(179,56,44,0.15); }

        /* Footer */
        .sidebar-footer { margin-top: auto; padding-top: 16px; border-top: 1px solid rgba(255,255,255,0.10); }

        /* ── Main viewport ── */
        .main-viewport { flex: 1; margin-left: var(--sidebar-w); display: flex; flex-direction: column; min-height: 100vh; }

        /* ── Header ── */
        .app-header {
          height: var(--header-h);
          display: flex; align-items: center; justify-content: space-between;
          padding: 0 40px; gap: 16px;
          position: sticky; top: 0; z-index: 300;
          background: var(--surface);
          border-bottom: 1px solid var(--border);
        }
        .header-search {
          display: flex; align-items: center; gap: 10px;
          background: var(--neutral-100); padding: 0 16px;
          border-radius: var(--radius-pill); height: 40px;
          flex: 1; max-width: 420px;
        }
        .header-search__icon { color: var(--text-muted); flex-shrink: 0; }
        .header-search__input {
          background: transparent; border: none; outline: none;
          width: 100%; font-size: 0.9375rem; font-family: var(--font-body);
          color: var(--text-body);
        }
        .header-search__input::placeholder { color: var(--text-faint); }
        .header-actions { display: flex; align-items: center; gap: 12px; }

        /* Icon button header */
        .header-icon-btn {
          background: none; border: none; cursor: pointer;
          color: var(--text-muted); position: relative;
          padding: 6px; border-radius: var(--radius-sm);
          display: flex; align-items: center; justify-content: center;
          transition: background var(--transition-fast);
        }
        .header-icon-btn:hover { background: var(--neutral-100); color: var(--text-body); }
        .notif-dot {
          position: absolute; top: 4px; right: 4px;
          width: 7px; height: 7px;
          background: var(--error); border-radius: 50%; border: 2px solid white;
        }

        /* Nuevo Lead btn */
        .header-new-lead-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 0 16px; height: 36px;
          background: var(--primary); color: white;
          border-radius: var(--radius-md); border: none;
          font-size: 0.875rem; font-weight: 600;
          cursor: pointer; transition: background var(--transition-fast);
        }
        .header-new-lead-btn:hover { background: var(--primary-hover); }

        /* Avatar header */
        .header-avatar {
          width: 36px; height: 36px; border-radius: 50%;
          background: var(--brand-800); color: #fff;
          display: flex; align-items: center; justify-content: center;
          font-size: 0.8125rem; font-weight: 700;
          flex-shrink: 0;
        }

        /* Notif dropdown */
        .notif-dropdown {
          position: absolute; top: calc(100% + 8px); right: 0;
          background: var(--surface); border: 1px solid var(--border);
          border-radius: var(--radius-lg); box-shadow: var(--shadow-pop);
          z-index: 9999; width: 280px; overflow: hidden;
        }
        .notif-dropdown__header { padding: 12px 16px; font-size: 0.875rem; font-weight: 700; color: var(--text-strong); border-bottom: 1px solid var(--border); }
        .notif-dropdown__empty  { padding: 24px 16px; text-align: center; font-size: 0.8125rem; color: var(--text-muted); }

        /* Page content */
        .page-content { padding: 32px 40px; flex: 1; }

        /* Payment alert */
        .payment-alert-banner {
          display: flex; align-items: center; gap: 10px;
          background: var(--error-bg); border-bottom: 1px solid rgba(179,56,44,0.2);
          color: var(--error); font-size: 0.875rem; font-weight: 500;
          padding: 10px 40px;
        }
        .payment-alert-link { color: var(--error); font-weight: 700; text-decoration: underline; }
        .payment-alert-link:hover { opacity: 0.8; }

        /* Mobile hamburger */
        .mobile-menu-btn { display: none; }
        .sidebar-overlay { display: none; }

        /* Modal */
        .modal-backdrop {
          position: fixed; inset: 0;
          background: rgba(33,30,25,0.45); backdrop-filter: blur(3px);
          display: flex; align-items: center; justify-content: center;
          z-index: 1000;
        }
        .modal-box {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: var(--radius-xl);
          padding: 28px;
          width: 100%; max-width: 480px;
          box-shadow: var(--shadow-pop);
        }
        .modal-header {
          display: flex; justify-content: space-between; align-items: center;
          margin-bottom: 24px;
        }
        .modal-header h2 {
          font-family: var(--font-display); font-size: var(--text-h3);
          font-weight: 600; color: var(--text-strong);
          letter-spacing: var(--tracking-tight);
        }
        .modal-close-btn {
          background: none; border: none; cursor: pointer;
          color: var(--text-muted); padding: 4px; border-radius: var(--radius-sm);
          display: flex; align-items: center; transition: background var(--transition-fast);
        }
        .modal-close-btn:hover { background: var(--neutral-100); }
        .modal-form  { display: flex; flex-direction: column; gap: 16px; }
        .form-field  { display: flex; flex-direction: column; gap: 6px; }
        .form-field label { font-size: 0.75rem; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.06em; }
        .form-field input {
          height: 40px; padding: 0 14px;
          border: 1px solid var(--border); border-radius: var(--radius-md);
          background: var(--surface); color: var(--text-strong);
          font-family: var(--font-body); font-size: 0.9375rem; outline: none;
          transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
        }
        .form-field input:focus { border-color: var(--interactive); box-shadow: var(--ring); }
        .form-row    { display: flex; gap: 12px; }
        .form-row .form-field { flex: 1; }
        .modal-footer { display: flex; justify-content: flex-end; gap: 10px; margin-top: 8px; }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .sidebar        { transform: translateX(-100%); }
          .sidebar--open  { transform: translateX(0); }
          .sidebar-overlay {
            display: block; position: fixed; inset: 0;
            background: rgba(0,0,0,0.4); z-index: 199;
          }
          .main-viewport  { margin-left: 0; }
          .app-header     { padding: 0 16px; }
          .page-content   { padding: 20px 16px; }
          .mobile-menu-btn {
            display: flex; position: fixed; top: 20px; left: 16px; z-index: 201;
            width: 40px; height: 40px; align-items: center; justify-content: center;
            background: var(--surface); border: 1px solid var(--border);
            border-radius: var(--radius-md); box-shadow: var(--shadow-card);
            cursor: pointer; color: var(--text-body);
          }
          .header-new-lead-btn span { display: none; }
          .header-search { max-width: none; }
          .payment-alert-banner { padding: 10px 16px; }
        }
      `}</style>
    </div>
  );
}
